import type { Event } from '@/mocks/admin';
import StatusBadge from '@/components/base/StatusBadge';

interface Props {
  event: Event;
  onClose: () => void;
}

const formatPrice = (n: number) => n === 0 ? 'Gratuit' : `${n.toLocaleString('fr-FR')} FCFA`;

const formatDateTime = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const formatDateShort = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const formatSeats = (seats?: number | null) => {
  if (seats == null) return 'Places non limitées';
  return `${seats.toLocaleString('fr-FR')} place${seats > 1 ? 's' : ''}`;
};

export default function EventDetailPanel({ event, onClose }: Props) {
  const fillRate = event.totalSeats > 0 ? Math.round((event.soldSeats / event.totalSeats) * 100) : 0;
  const revenue = event.soldSeats * event.ticketPrice;
  const remainingSeats = event.totalSeats - event.soldSeats;
  const ticketTypes = event.ticketTypes?.length
    ? event.ticketTypes
    : [{ name: 'Standard', price: event.ticketPrice, seats: event.totalSeats, description: '' }];

  const statusConfig: Record<Event['status'], { bg: string; border: string; text: string; icon: string; msg: string }> = {
    pending: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'ri-time-line', msg: 'Votre événement est en cours d\'examen par l\'administrateur.' },
    approved: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'ri-checkbox-circle-line', msg: 'Votre événement est publié et accessible au public.' },
    rejected: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'ri-close-circle-line', msg: 'Votre événement a été rejeté. Voir la raison ci-dessous.' },
    deleted: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', icon: 'ri-delete-bin-line', msg: 'Cet événement est dans la corbeille et n’est plus visible dans l’application.' },
  };
  const sc = statusConfig[event.status] ?? statusConfig.pending;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white z-50 flex flex-col shadow-2xl">

        {/* Hero image */}
        <div className="relative h-44 flex-shrink-0 overflow-hidden">
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
          {/* Type badge over image */}
          <div className="absolute bottom-3 left-4">
            <span className="text-xs font-bold bg-gold text-white px-2.5 py-1 rounded-full">
              {event.type}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* Title + status */}
          <div className="px-5 pt-4 pb-3 border-b border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-base font-bold text-navy leading-tight flex-1">{event.title}</h2>
              <div className="flex-shrink-0">
                <StatusBadge status={event.status} size="sm" />
              </div>
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <i className="ri-map-pin-line text-gold"></i>
              {event.location}
            </p>
          </div>

          <div className="px-5 py-4 space-y-5">

            {/* Status banner */}
            <div className={`flex items-start gap-3 p-3 rounded-xl border ${sc.bg} ${sc.border}`}>
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${sc.bg}`}>
                <i className={`${sc.icon} ${sc.text} text-base`}></i>
              </div>
              <p className={`text-xs font-medium ${sc.text} leading-relaxed`}>{sc.msg}</p>
            </div>

            {/* Rejection reason */}
            {event.status === 'rejected' && event.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  <p className="text-xs font-bold text-red-700">Raison du rejet</p>
                </div>
                <p className="text-sm text-red-700 leading-relaxed">{event.rejectionReason}</p>
              </div>
            )}

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-navy rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-white">{event.soldSeats.toLocaleString('fr-FR')}</p>
                <p className="text-[10px] text-white/60 mt-0.5">Tickets vendus</p>
              </div>
              <div className="bg-gold rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-white">{fillRate}%</p>
                <p className="text-[10px] text-white/70 mt-0.5">Taux remplissage</p>
              </div>
              <div className="bg-emerald-500 rounded-xl p-3 text-center">
                <p className="text-base font-bold text-white leading-tight">{revenue === 0 ? '—' : `${(revenue / 1000).toFixed(0)}k`}</p>
                <p className="text-[10px] text-white/70 mt-0.5">Revenus FCFA</p>
              </div>
            </div>

            {/* Fill rate bar */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-600">Remplissage</span>
                <span className="text-xs text-gray-400">
                  <strong className="text-navy">{event.soldSeats}</strong>/{event.totalSeats} places
                  {remainingSeats > 0 && <span className="text-emerald-600"> · {remainingSeats} disponibles</span>}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${fillRate >= 80 ? 'bg-emerald-500' : fillRate >= 50 ? 'bg-gold' : 'bg-navy/40'}`}
                  style={{ width: `${fillRate}%` }}
                />
              </div>
            </div>

            {/* Key details */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Informations</p>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { label: 'Date de début', value: formatDateShort(event.date), icon: 'ri-calendar-line' },
                  { label: 'Date de fin', value: formatDateShort(event.endDate), icon: 'ri-calendar-check-line' },
                  { label: 'Lieu', value: event.location, icon: 'ri-map-pin-line' },
                  { label: 'Prix ticket', value: formatPrice(event.ticketPrice), icon: 'ri-ticket-line' },
                  { label: 'Capacité totale', value: `${event.totalSeats.toLocaleString('fr-FR')} places`, icon: 'ri-group-line' },
                  { label: "Nom de l'organisation", value: event.organizerName, icon: 'ri-user-star-line' },
                  { label: 'Contact', value: event.contact, icon: 'ri-phone-line' },
                ].map((item) => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-gold text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-400 font-medium">{item.label}</p>
                      <p className="text-xs font-bold text-navy mt-0.5 leading-tight break-words">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ticket categories */}
            {ticketTypes.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Catégories de tickets</p>
                <div className="space-y-2.5">
                  {ticketTypes.map((ticketType, index) => (
                    <div key={`${ticketType.id ?? ticketType.name}-${index}`} className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-navy">{ticketType.name}</p>
                          {ticketType.description ? (
                            <p className="mt-1 text-xs leading-5 text-gray-500">{ticketType.description}</p>
                          ) : null}
                        </div>
                        <span className="rounded-full bg-gold/10 px-3 py-1 text-xs font-bold text-gold-dark">
                          {formatPrice(ticketType.price)}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-lg bg-gray-50 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Prix</p>
                          <p className="mt-1 text-xs font-bold text-navy">{formatPrice(ticketType.price)}</p>
                        </div>
                        <div className="rounded-lg bg-gray-50 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Places</p>
                          <p className="mt-1 text-xs font-bold text-navy">{formatSeats(ticketType.seats)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Horaires complets */}
            <div className="bg-navy/5 rounded-xl p-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Horaires</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-navy rounded-lg flex-shrink-0">
                    <i className="ri-play-circle-line text-white text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Début</p>
                    <p className="text-xs font-semibold text-navy capitalize">{formatDateTime(event.date)}</p>
                  </div>
                </div>
                <div className="ml-4 w-0.5 h-3 bg-gray-200 rounded-full"></div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-lg flex-shrink-0">
                    <i className="ri-stop-circle-line text-gray-500 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Fin</p>
                    <p className="text-xs font-semibold text-navy capitalize">{formatDateTime(event.endDate)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Description</p>
              <p className="text-sm text-gray-700 leading-relaxed">{event.description}</p>
            </div>

            {/* Revenue detail (if approved) */}
            {event.status === 'approved' && event.ticketPrice > 0 && (
              <div className="bg-gradient-to-r from-navy to-navy-lighter rounded-xl p-4 text-white">
                <p className="text-xs font-bold text-white/60 uppercase tracking-wide mb-3">Revenus estimés</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-lg font-bold">{revenue.toLocaleString('fr-FR')}</p>
                    <p className="text-[10px] text-white/60">FCFA actuels</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{(event.totalSeats * event.ticketPrice).toLocaleString('fr-FR')}</p>
                    <p className="text-[10px] text-white/60">FCFA potentiels</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{fillRate}%</p>
                    <p className="text-[10px] text-white/60">Réalisé</p>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex-1 text-xs text-gray-400">
              <i className="ri-calendar-event-line mr-1"></i>
              Soumis le {new Date(event.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-navy text-white text-sm font-semibold rounded-xl hover:bg-navy-lighter cursor-pointer whitespace-nowrap"
            >
              Fermer
            </button>
          </div>
        </div>

      </div>
    </>
  );
}
